import React from "react";

/**
 * Hand-drawn / graffiti-style decorative SVGs for the marketing site.
 *
 * Each component is a pure, prop-driven SVG that:
 *   * inherits color from `currentColor` (so you can theme it with Tailwind's
 *     `text-` classes) unless you pass an explicit `stroke` / `fill`.
 *   * takes a `className` so callers control sizing / positioning entirely
 *     via Tailwind utilities.
 *
 * Pieces:
 *   <BrushStroke />     wide flat yellow brush underline / highlight
 *   <Squiggle />        bouncy hand-drawn underline
 *   <Starburst />       4-point spark (used near headlines)
 *   <Doodle.Crown />, <Doodle.Heart />, <Doodle.Bolt />, <Doodle.X />,
 *   <Doodle.Arrow />    chunky marker-style doodles
 *   <TornEdge />        ripped paper top/bottom decoration
 *   <HalftoneDots />    repeating dot grid (fades to transparent)
 */

const baseSvgProps = (className) => ({
  className,
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: "false",
});

// Flat horizontal yellow brush highlight, used behind / under accent words.
export const BrushStroke = ({ className = "" }) => (
  <svg viewBox="0 0 400 60" preserveAspectRatio="none" className={className} aria-hidden focusable="false">
    <path
      d="M5 32 C 70 14, 160 50, 230 26 S 360 12, 395 30 L 395 50 C 320 38, 240 60, 160 44 S 50 56, 5 50 Z"
      fill="currentColor"
    />
  </svg>
);

// Squiggly hand-drawn underline.
export const Squiggle = ({ className = "", strokeWidth = 5 }) => (
  <svg viewBox="0 0 200 24" preserveAspectRatio="none" {...baseSvgProps(className)} strokeWidth={strokeWidth}>
    <path d="M2 14 Q 18 0, 35 12 T 70 14 T 105 12 T 140 14 T 175 12 T 198 14" />
  </svg>
);

// Compact 4-point sparkle / spark.
export const Starburst = ({ className = "", strokeWidth = 3 }) => (
  <svg viewBox="0 0 40 40" {...baseSvgProps(className)} strokeWidth={strokeWidth}>
    <path d="M20 4 L23 17 L36 20 L23 23 L20 36 L17 23 L4 20 L17 17 Z" fill="currentColor" stroke="none" />
  </svg>
);

// Doodle namespace — chunky marker icons.
export const Doodle = {
  Crown: ({ className = "", strokeWidth = 3 }) => (
    <svg viewBox="0 0 64 48" {...baseSvgProps(className)} strokeWidth={strokeWidth}>
      <path d="M6 38 L10 14 L22 28 L32 8 L42 28 L54 14 L58 38 Z" />
      <path d="M6 44 L58 44" />
      <circle cx="32" cy="6" r="2" fill="currentColor" stroke="none" />
      <circle cx="10" cy="12" r="2" fill="currentColor" stroke="none" />
      <circle cx="54" cy="12" r="2" fill="currentColor" stroke="none" />
    </svg>
  ),
  Heart: ({ className = "", strokeWidth = 4 }) => (
    <svg viewBox="0 0 48 48" {...baseSvgProps(className)} strokeWidth={strokeWidth}>
      <path d="M24 42 C 6 30, 4 14, 14 10 C 19 8, 24 12, 24 18 C 24 12, 29 8, 34 10 C 44 14, 42 30, 24 42 Z" />
    </svg>
  ),
  Bolt: ({ className = "", strokeWidth = 3 }) => (
    <svg viewBox="0 0 32 48" {...baseSvgProps(className)} strokeWidth={strokeWidth}>
      <path d="M18 2 L4 26 L14 26 L10 46 L28 22 L18 22 Z" fill="currentColor" stroke="none" />
    </svg>
  ),
  X: ({ className = "", strokeWidth = 5 }) => (
    <svg viewBox="0 0 32 32" {...baseSvgProps(className)} strokeWidth={strokeWidth}>
      <path d="M6 6 L26 26 M26 6 L6 26" />
    </svg>
  ),
  Arrow: ({ className = "", strokeWidth = 4 }) => (
    <svg viewBox="0 0 64 32" {...baseSvgProps(className)} strokeWidth={strokeWidth}>
      <path d="M4 16 C 16 4, 40 4, 56 16" />
      <path d="M48 8 L58 16 L52 26" />
    </svg>
  ),
  Scribble: ({ className = "", strokeWidth = 4 }) => (
    <svg viewBox="0 0 80 40" {...baseSvgProps(className)} strokeWidth={strokeWidth}>
      <path d="M4 30 C 14 6, 26 38, 38 12 S 60 38, 76 14" />
    </svg>
  ),
  Star: ({ className = "", strokeWidth = 3 }) => (
    <svg viewBox="0 0 48 48" {...baseSvgProps(className)} strokeWidth={strokeWidth}>
      <path d="M24 4 L29 18 L44 19 L32 29 L36 44 L24 36 L12 44 L16 29 L4 19 L19 18 Z" />
    </svg>
  ),
};

// Ripped-paper edge as a CSS clip-path applied to a coloured strip.
// Use as <TornEdge side="bottom" className="bg-black h-10" /> immediately
// after a coloured section; the jagged strip visually "rips" the next.
export const TornEdge = ({ side = "bottom", className = "" }) => {
  // 20 zig-zag points across the width.
  const W = 100;
  const peaks = 22;
  const pts = [];
  for (let i = 0; i <= peaks; i += 1) {
    const x = (i * W) / peaks;
    // Pseudo-random but stable jitter so each peak height varies.
    const seed = Math.sin(i * 12.9898) * 43758.5453;
    const jitter = (seed - Math.floor(seed)) * 0.7 + 0.3; // 0.3..1
    const yTop = jitter * 60; // 0..60% from top
    pts.push([x, yTop]);
  }
  // Build clip-path that creates a torn top/bottom edge.
  const polyTop = `polygon(${pts.map(([x, y]) => `${x.toFixed(2)}% ${y.toFixed(2)}%`).join(", ")}, 100% 100%, 0% 100%)`;
  const polyBottom = `polygon(0% 0%, 100% 0%, ${pts
    .map(([x, y]) => `${x.toFixed(2)}% ${(100 - y).toFixed(2)}%`)
    .reverse()
    .join(", ")})`;
  const clipPath = side === "top" ? polyTop : polyBottom;
  return <div className={className} style={{ clipPath }} aria-hidden />;
};

// Faded halftone dot grid — fixed-position background accent.
export const HalftoneDots = ({ className = "" }) => (
  <svg viewBox="0 0 200 200" className={className} aria-hidden focusable="false">
    <defs>
      <pattern id="halftone-dot-pattern" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
        <circle cx="5" cy="5" r="1.4" fill="currentColor" />
      </pattern>
      <radialGradient id="halftone-mask">
        <stop offset="0%" stopColor="white" stopOpacity="1" />
        <stop offset="100%" stopColor="white" stopOpacity="0" />
      </radialGradient>
      <mask id="halftone-fade">
        <rect width="200" height="200" fill="url(#halftone-mask)" />
      </mask>
    </defs>
    <rect width="200" height="200" fill="url(#halftone-dot-pattern)" mask="url(#halftone-fade)" />
  </svg>
);
